import { useState, useMemo, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import TransportService from './components/ExpedieriPageComponents/TransportService';
import TransportStats from './components/ExpedieriPageComponents/TransportStats';
import TransportFilters from './components/ExpedieriPageComponents/TransportFilters';

import TransportMobileCards from './components/ExpedieriPageComponents/TransportMobileCards';
import AddEditTransportModal from '../admin/components/modals/transports/AddEditTransportModal';
import DeleteTransportModal from '../admin/components/modals/transports/TransportDeleteModal';
import TransportViewModal from '../admin/components/modals/transports/TransportViewModal';

import RefreshButton from '@/components/reload/RefreshButton';
import LocationShareModal from './components/ExpedieriPageComponents/LocationShareModal';
import TransportTable from './components/ExpedieriPageComponents/TransportTable';

const ExpedieriPage = () => {
  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLocationShareModal, setShowLocationShareModal] = useState(false);
  const [selectedTransport, setSelectedTransport] = useState(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    id: '',
    client: '',
    destinatie: '',
    status: 'in_asteptare',
    prioritate: 'normala',
    descriere: '',
    value: '',
    ora_plecarii: '',
    ora_estimata: '',
    progres: 0,
    sofer_id: null,
    vehicul_id: null,
    client_id: null,
    locatie_plecare_id: null,
    locatie_destinatie_id: null,
    tip_plata: ''
  });

  const transportService = new TransportService();

  // Fetch transports data
  const fetchTransports = async () => {
    setLoading(true);
    try {
      const data = await transportService.fetchTransports();
      setTransports(data);
    } catch (error) {
      console.error('Eroare la încărcarea transporturilor:', error);
      alert('Eroare la încărcarea transporturilor: ' + error.message);
      setTransports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransports();
    const interval = setInterval(fetchTransports, 600000); // 10 minute
    return () => clearInterval(interval);
  }, []);

  // Memoized filtered transports
  const filteredTransports = useMemo(() => {
    return transports.filter(transport => {
      const matchesSearch = [
        transport.client,
        transport.ruta,
        transport.id,
        transport.driver,
        transport.vehicle,
        transport.tip_plata
      ].some(field => 
        field?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesFilter = selectedFilter === 'all' || transport.status === selectedFilter;
      return matchesSearch && matchesFilter;
    });
  }, [transports, searchTerm, selectedFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = transports.length;
    const completed = transports.filter(t => t.status === 'livrat').length;
    const inTransit = transports.filter(t => t.status === 'pe_drum').length;
    const inLoading = transports.filter(t => t.status === 'in_incarcare').length;
    const pending = transports.filter(t => t.status === 'in_asteptare').length;
    const totalValue = transports.reduce((sum, t) => sum + Number(t.value), 0);
    
    return { total, completed, inTransit, pending, inLoading, totalValue };
  }, [transports]);

  // Modal handlers
  const handleShareLocation = (transport) => {
    setSelectedTransport(transport);
    setShowLocationShareModal(true);
  };

  const handleView = async (transport) => {
    try {
      const detailedTransport = await transportService.fetchTransportDetails(transport.id);
      setSelectedTransport(detailedTransport);
      setShowViewModal(true);
    } catch (err) {
      console.error('Eroare la detalii:', err);
      alert('Nu s-au putut încărca detaliile transportului.');
    }
  };

  const handleEdit = (transport) => {
    setSelectedTransport(transport);
    const destinatie = transport.route?.includes(' → ') 
      ? transport.route.split(' → ')[1] 
      : transport.endLocation || transport.route;
      
    setFormData({
      id: transport.id,
      client: transport.client,
      destinatie: destinatie,
      status: transport.status,
      prioritate: transport.prioritate || 'normala',
      descriere: transport.description,
      value: transport.value?.toString() || '',
      ora_plecarii: transport.ora_plecarii ? transport.ora_plecarii.split('T')[0] : transport.date,
      ora_estimata: transport.ora_estimata ? transport.ora_estimata.split('T')[0] : '',
      progres: transport.progres || 0,
      sofer_id: transport.sofer_id || null,
      vehicul_id: transport.vehicul_id || null,
      client_id: transport.client_id || null,
      locatie_plecare_id: transport.locatie_plecare_id || null,
      locatie_destinatie_id: transport.locatie_destinatie_id || null,
      tip_plata: transport.tip_plata || 'cash'
    });
    
    setShowEditModal(true);
  };

  const handleDelete = (transport) => {
    setSelectedTransport(transport);
    setShowDeleteModal(true);
  };

  const handleAdd = () => {
    const newId = `TR${Date.now().toString().slice(-6)}`;
    setFormData({
      id: newId,
      client: '',
      destinatie: '',
      status: 'in_asteptare',
      prioritate: 'normala',
      descriere: '',
      value: '',
      ora_plecarii: new Date().toISOString().split('T')[0] + 'T08:00',
      ora_estimata: '',
      progres: 0,
      sofer_id: null,
      vehicul_id: null,
      client_id: null,
      locatie_plecare_id: null,
      locatie_destinatie_id: null,
      tip_plata: ''
    });
    setShowAddModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await transportService.updateTransport(selectedTransport.id, formData);
      await fetchTransports();
      closeAllModals();
    } catch (error) {
      console.error('Eroare la actualizare:', error);
      alert('Eroare la actualizare: ' + error.message);
    }
  };

  const handleSaveAdd = async () => {
    try {
      await transportService.createTransport(formData);
      await fetchTransports();
      closeAllModals();
    } catch (error) {
      console.error('Eroare la adăugare transport:', error);
      alert('Eroare la adăugare transport: ' + error.message);
    }
  };

  const confirmDelete = async () => {
    try {
      await transportService.deleteTransport(selectedTransport.id);
      await fetchTransports();
      setShowDeleteModal(false);
      setSelectedTransport(null);
      alert('Transport șters cu succes!');
    } catch (error) {
      console.error('Eroare la ștergere:', error);
      alert('Eroare la ștergerea transportului: ' + error.message);
    }
  };

  const closeAllModals = () => {
    setShowViewModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowAddModal(false);
    setShowLocationShareModal(false);
    setSelectedTransport(null);
    
    setFormData({
      id: '',
      client: '',
      destinatie: '',
      status: 'in_asteptare',
      prioritate: 'normala',
      descriere: '',
      value: '',
      ora_plecarii: '',
      ora_estimata: '',
      progres: 0,
      sofer_id: null,
      vehicul_id: null,
      client_id: null,
      locatie_plecare_id: null,
      locatie_destinatie_id: null,
      tip_plata: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Se încarcă transporturile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-6 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 py-4 max-w-full">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="min-w-0 flex-1">
            

            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <RefreshButton onRefresh={fetchTransports} />
              <button 
                onClick={handleAdd}
                className="bg-green-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl flex items-center hover:from-blue-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
              >
                <Plus className="w-10 h-10 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Transport Nou</span>
                <span className="xs:hidden">Nou</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 sm:px-4 lg:px-6 space-y-4 sm:space-y-6 mt-4 sm:mt-6 max-w-full">
        {/* Statistics */}
        <div className="w-full overflow-hidden">
          <TransportStats stats={stats} />
        </div>

        {/* Filters */}
        <div className="w-full overflow-hidden">
          <TransportFilters 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            showMobileFilters={showMobileFilters}
            setShowMobileFilters={setShowMobileFilters}
          />
        </div>

        {/* Results Count */}
        <div className="flex justify-between items-center">
          <p className="text-xs sm:text-sm text-gray-500">
            {filteredTransports.length} din {transports.length} transporturi
          </p>
        </div>

        {/* Desktop Table - Hidden on small screens */}
        <div className="hidden lg:block w-full overflow-x-auto">
          <TransportTable 
            transports={filteredTransports}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onShareLocation={handleShareLocation}
          />
        </div>

        {/* Mobile Cards - Shown on small screens */}
        <div className="lg:hidden w-full">
          <TransportMobileCards 
            transports={filteredTransports}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onShareLocation={handleShareLocation}
          />
        </div>

        {/* Empty State */}
        {filteredTransports.length === 0 && !loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-12 text-center mx-auto max-w-full">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Nu s-au găsit transporturi</h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 px-4">
              {searchTerm || selectedFilter !== 'all' 
                ? 'Încercați să modificați filtrele de căutare'
                : 'Începeți prin a adăuga primul transport'
              }
            </p>
            {!searchTerm && selectedFilter === 'all' && (
              <button 
                onClick={handleAdd}
                className="bg-gradient-to-r from-blue-500 to-red-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl hover:from-blue-600 hover:to-red-600 transition-colors text-sm sm:text-base"
              >
                <Plus className="w-4 h-4 mr-2 inline" />
                Adaugă primul transport
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <TransportViewModal
        show={showViewModal}
        onClose={closeAllModals}
        transport={selectedTransport}
        onShareLocation={handleShareLocation}
        onEditTransport={handleEdit}
      />

      <AddEditTransportModal
        showAdd={showAddModal}
        showEdit={showEditModal}
        selectedTransport={selectedTransport}
        formData={formData}
        setFormData={setFormData}
        onClose={closeAllModals}
        onSaveAdd={handleSaveAdd}
        onSaveEdit={handleSaveEdit}
      />

      <DeleteTransportModal
        show={showDeleteModal}
        transport={selectedTransport}
        onClose={closeAllModals}
        onConfirm={confirmDelete}
      />

      <LocationShareModal
        show={showLocationShareModal}
        transport={selectedTransport}
        onClose={closeAllModals}
      />
    </div>
  );
};

export default ExpedieriPage;